import { useCallback, useEffect, useMemo, useState } from "react";
import { Pagination, Table } from "rsuite";
import { PaginationEntriesInput } from "../../components/paginationEntries";
import { ActionsInvoiceTable, useAppSelector } from "../../core/redux";
import { useAppDispatch } from "../../core/redux";
import { TextCell } from "../../components/tableItem.component";
import { useAlertDialog } from "../../components/alertDialog";
import { ApiSchemaInvoice, ApiSchemaTaskDate } from "../../models/api/invoice";
import { ApiInvoice } from "../../core/apis/invoice";
import { useHistory } from "react-router-dom";
import { AppRouteUi, AppRouteApi } from "../../config/appRoute";
import { LibExport } from "../../libs/exportDownload";
import { SendMail } from "../../components/sendMail.component";
import { sortTableData } from "../../utils/tableFilter";
import { ActionsModal } from "../../core/redux/slices/sliceModal";
import { EnumModals } from "../../libs";
import { ModalData } from "../../components/modal";
import { ApiSettings } from "../../core/apis";
import { convertToTwoDecimal } from "../../utils/convertToTwoDecimal";
import {
  FaUser,
  FaCalendarAlt,
  FaClock,
  FaCog,
  FaRegCalendar,
  FaCalendarWeek,
  FaRegCalendarAlt,
} from "react-icons/fa";
import { BiTime, BiCalendar } from "react-icons/bi";
import {
  FiEdit,
  FiTrash2,
  FiDownload,
  FiFileText,
  FiClock,
} from "react-icons/fi";
import { EnumInvoiceGenerationInvokeType } from "../../libs/enums/EnumPatientInvoketype";
import { UtilityDate } from "../../utils/dateUtils";

const { Column, HeaderCell, Cell } = Table;

type TableData = ApiSchemaInvoice & {
  name?: string;
  caregiverName?: string;
  totalTimeWorkedInMinuteStr?: string;
  totalTimeWorkedInMinute?: number;
  totalMaxHours?: number;
  totalMaxHoursStr?: string;
  generationType?: EnumInvoiceGenerationInvokeType;
  stageStatus?: number; // Add this field for sorting
};

type TypeTableKey = keyof TableData;

type InvoiceListProps = {
  isValidating?: boolean;
  data?: ApiSchemaInvoice[];
  totalCount: number;
};

export const InvoiceList = ({
  data,
  isValidating,
  totalCount,
}: InvoiceListProps) => {
  const [tableData, setTableData] = useState<TableData[]>([]);
  const { alertDialogCtrl, alertDialogComponent } = useAlertDialog();
  const { pageNumber, pageSize, isLoading } = useAppSelector((state) => ({
    ...state.invoiceTable,
    startDate: state.InvoiceFilter.StartDate,
    endDate: state.InvoiceFilter.EndDate,
  }));
  const dispatch = useAppDispatch();
  const history = useHistory();
  const [sortBy, setSortBy] = useState<TypeTableKey>("" as TypeTableKey);
  const [sortAsc, setSortAsc] = useState(true);
  const [haveEmail, setHaveEmail] = useState(false);
  const [isLoadingEmailData, setIsLoadingEmailData] = useState(false);

  const getEmailData = useCallback(async () => {
    setIsLoadingEmailData(true);
    const response = await ApiSettings.GetSenderEmail();
    if (response.isSuccess && response.data?.senderEmail) {
      setHaveEmail(true);
    }
    setIsLoadingEmailData(false);
  }, []);

  const getDaysBetweenDates = (startDate: Date, endDate: Date) => {
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatMaxHours = (totalHours: number) => {
    const hours = Math.floor(totalHours);
    const minutes = Math.floor((totalHours - hours) * 60);
    const seconds = Math.floor(((totalHours - hours) * 60 - minutes) * 60);
    return `${hours}h ${minutes}m ${seconds}s`;
  };

  const calculateTotalMaxHours = (
    startDate: string,
    endDate: string,
    maxHoursPerDay: number
  ) => {
    const totalDays = getDaysBetweenDates(
      new Date(startDate),
      new Date(endDate)
    );
    return totalDays * maxHoursPerDay;
  };

  const handleDelete = useCallback(
    async (rowData: ApiSchemaInvoice) => {
      alertDialogCtrl.open({
        message: "Are you sure you want to delete this Invoice?",
        proceedBtnText: "Delete",
        type: "danger",
        onSuccess: async () => {
          dispatch(ActionsInvoiceTable.setIsLoading(true));
          const response = await ApiInvoice.DeleteById(rowData.sKey!);
          if (response.isSuccess) {
            dispatch(ActionsInvoiceTable.setRefetch(true));
            return;
          }
          dispatch(ActionsInvoiceTable.setIsLoading(false));
        },
      });
    },
    [alertDialogCtrl, dispatch]
  );

  useEffect(
    () => {
      const tableData = data?.map((invoice: TableData) => {
        const { totalAmount, ...rest } = invoice;

        const totalMaxHours = calculateTotalMaxHours(
          invoice.startDate!,
          invoice.endDate!,
          invoice.maxHours
        );

        const totalWorkTimeInMinute = invoice.totalAssignedTimeInMinute;

        const obj: TableData = {
          name: invoice.patientFirstName + " " + invoice.patientLastName,
          caregiverName:
            invoice.caregiverFirstName + " " + invoice.caregiverLastName,
          totalTimeWorkedInMinute: totalWorkTimeInMinute,
          totalTimeWorkedInMinuteStr: formatMaxHours(
            totalWorkTimeInMinute / 60
          ),
          totalMaxHours,
          totalMaxHoursStr: formatMaxHours(totalMaxHours),
          totalAmount: convertToTwoDecimal(totalAmount! as unknown as number),
          ...rest,
        };
        return obj;
      });

      setTableData(tableData ?? []);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  function ProceedCell({
    rowData,
    dataKey,
    ...props
  }: {
    rowData?: TableData;
    dataKey?: string;
  }) {
    // Add stageStatus calculation
    if (rowData && !rowData.stageStatus) {
      rowData.stageStatus = rowData.isSubmitted
        ? 2
        : rowData.isVerified
        ? 1
        : 0;
    }

    const getColorStyleForProceedButton = () => {
      // if (
      //   rowData?.totalTimeWorkedInMinute! < rowData?.totalAssignedTimeInMinute!
      // )
      //   return "text-blue border border-success";
      // if (
      //   rowData?.totalTimeWorkedInMinute! > rowData?.totalAssignedTimeInMinute!
      // )
      //   return "text-warning border border-success";

      return "bg-green-200 text-white";
    };

    return (
      <Cell {...props} dataKey={dataKey}>
        {rowData?.isSubmitted ? (
          <p className="text-green-200 border d-inline px-2 rounded-pill border-green-200">
            Submitted
          </p>
        ) : rowData?.isVerified ? (
          <p
            className={`${getColorStyleForProceedButton()} d-inline p-1 px-2 rounded-pill click`}
            // title={
            //   rowData?.totalTimeWorkedInMinute! <
            //   rowData?.totalAssignedTimeInMinute!
            //     ? "Caregiver worked less than assigned time"
            //     : "Caregiver worked more than assigned time"
            // }
            onClick={() => {
              alertDialogCtrl.open({
                message: "Are you sure you want submit this Invoice?",
                proceedBtnText: "Submit",
                type: "danger",
                onSuccess: async () => {
                  dispatch(ActionsInvoiceTable.setIsLoading(true));
                  const response = await ApiInvoice.SubmitInvoice(
                    rowData?.sKey!
                  );
                  if (response.isSuccess) {
                    dispatch(ActionsInvoiceTable.setRefetch(true));
                    return;
                  }
                  dispatch(ActionsInvoiceTable.setIsLoading(false));
                },
              });
            }}
          >
            Send to finance
          </p>
        ) : (
          <p className="text-danger d-inline p-1 px-2 border border-success rounded-pill">
            Missing-Invoice
          </p>
        )}
      </Cell>
    );
  }

  const filteredTableData = useMemo(() => {
    return sortTableData(tableData, sortBy, sortAsc);
  }, [tableData, sortBy, sortAsc]);

  useEffect(() => {
    const taskDates = filteredTableData
      .filter((x) => !!x.taskDates)
      .flatMap((x) => x.taskDates) as ApiSchemaTaskDate[];
    const appointIds = taskDates.map((x) => x.appointmentId!);
    dispatch(ActionsInvoiceTable.setRowSkeys(appointIds));
  }, [filteredTableData, dispatch]);

  const openInvoiceDownloadModal = (row: TableData) => {
    const invoice = data?.find((x) => x.sKey === row.sKey);
    if (!invoice) return;
    ModalData.InvoiceDownloadModal.startDate = invoice.startDate;
    ModalData.InvoiceDownloadModal.endDate = invoice.endDate;
    ModalData.InvoiceDownloadModal.handleDownload = (
      selectedYearMonth: Date
    ) => {
      LibExport.DownloadExport(
        `${AppRouteApi.Invoice.Excel(
          invoice.sKey
        )}&invoiceYearMonth=${selectedYearMonth?.toISOString()}`,
        `Invoice-${invoice.patientFirstName}-${invoice.patientLastName}.xlsx`
      );
      dispatch(ActionsModal.hideModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
    };

    dispatch(ActionsModal.openModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
  };

  const openInvoicePdfDownloadModal = (row: TableData) => {
    const invoice = data?.find((x) => x.sKey === row.sKey);
    if (!invoice) return;
    ModalData.InvoiceDownloadModal.startDate = invoice.startDate;
    ModalData.InvoiceDownloadModal.endDate = invoice.endDate;
    ModalData.InvoiceDownloadModal.handleDownload = (
      selectedYearMonth: Date
    ) => {
      LibExport.DownloadExport(
        `${AppRouteApi.Invoice.Pdf(
          invoice.sKey
        )}&invoiceYearMonth=${selectedYearMonth?.toISOString()}`,
        `Invoice-${invoice.patientFirstName}-${invoice.patientLastName}.pdf`
      );
      dispatch(ActionsModal.hideModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
    };

    dispatch(ActionsModal.openModal(EnumModals.INVOICE_DOWNLOAD_MODAL));
  };

  useEffect(() => {
    getEmailData();
  }, [getEmailData]);

  return (
    <div>
      {alertDialogComponent}
      <Table
        autoHeight
        rowClassName="table-row-hover"
        bordered
        cellBordered
        hover
        classPrefix="rs-table"
        className="table-striped"
        data={
          !totalCount
            ? filteredTableData.filter(
                (val, idx) =>
                  idx >= (pageNumber - 1) * pageSize &&
                  idx < pageNumber * pageSize
              )
            : filteredTableData
        }
        loading={isValidating || isLoading || isLoadingEmailData}
        renderEmpty={() => (
          <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
            <h3 className="fw-light">No Data Available</h3>
          </div>
        )}
        sortColumn={sortBy}
        sortType={sortAsc ? "asc" : "desc"}
        onSortColumn={(sortBy, sortAsc) => {
          setSortBy(sortBy as keyof TableData);
          setSortAsc(sortAsc === "asc");
        }}
      >
        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaUser className="me-2" /> Patient
            </span>
          </HeaderCell>
          <TextCell component="Invoice" dataKey="name" />
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaUser className="me-2" /> Caregiver
            </span>
          </HeaderCell>
          <TextCell component="Invoice" dataKey="caregiverName" />
        </Column>

        <Column flexGrow={0.8} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaCalendarAlt className="me-2" /> Start
            </span>
          </HeaderCell>
          <Cell dataKey="startDate">
            {(rowData: TableData) => (
              <span>
                {UtilityDate.formatLocalDateOnly(
                  rowData.startDate!
                )}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.8} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaCalendarAlt className="me-2" /> End
            </span>
          </HeaderCell>
          <Cell dataKey="endDate">
            {(rowData: TableData) => (
              <span>
                {UtilityDate.formatDateWithoutTimezoneMinusOne(
                  rowData.endDate!
                )}
              </span>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.6} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <BiCalendar className="me-2" /> Days
            </span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex align-items-center">
                <BiCalendar className="me-1 text-info" />
                <span>
                  {getDaysBetweenDates(
                    new Date(rowData.startDate!),
                    new Date(rowData.endDate!)
                  )}
                </span>
              </div>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.8} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <BiTime className="me-2" /> Worked
            </span>
          </HeaderCell>
          <Cell dataKey="totalTimeWorkedInMinute">
            {(rowData: TableData) => (
              <div className="d-flex align-items-center">
                <FaClock className="me-1 text-primary" />
                <span
                  className={
                    rowData.totalTimeWorkedInMinute! / 60 >
                    calculateTotalMaxHours(
                      rowData.startDate!,
                      rowData.endDate!,
                      rowData.maxHours
                    )
                      ? "text-danger"
                      : "text-success"
                  }
                >
                  {rowData.totalTimeWorkedInMinuteStr}
                </span>
              </div>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.8} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <BiTime className="me-2" /> Max Hours
            </span>
          </HeaderCell>
          <Cell dataKey="totalMaxHours">
            {(rowData: TableData) => (
              <div className="d-flex align-items-center">
                <FaClock className="me-1 text-warning" />
                <span>{rowData.totalMaxHoursStr}</span>
              </div>
            )}
          </Cell>
        </Column>

        <Column flexGrow={0.8} sortable>
          <HeaderCell className="fw-bold text-black">
            <span className="d-inline-flex align-items-center">
              <FaCog className="me-2" /> Invoice Type
            </span>
          </HeaderCell>
          <Cell dataKey="generationType">
            {(rowData: TableData) => {
              const type =
                rowData.generationType as EnumInvoiceGenerationInvokeType;
              const getIcon = () => {
                switch (type) {
                  case EnumInvoiceGenerationInvokeType.Daily:
                    return <FaRegCalendar className="text-primary" />;
                  case EnumInvoiceGenerationInvokeType.Weekly:
                    return <FaCalendarWeek className="text-success" />;
                  case EnumInvoiceGenerationInvokeType.Biweekly:
                    return <FaCalendarWeek className="text-warning" />;
                  case EnumInvoiceGenerationInvokeType.Monthly:
                    return <FaRegCalendarAlt className="text-info" />;
                  default:
                    return <FaCog />;
                }
              };
              return (
                <div className="d-flex align-items-center">
                  {getIcon()}
                  <span className="ms-2">{type}</span>
                </div>
              );
            }}
          </Cell>
        </Column>

        <Column flexGrow={1} sortable>
          <HeaderCell className="fw-bold text-black">
            <span title="Stage">Stage</span>
          </HeaderCell>
          <ProceedCell dataKey="stageStatus" />
        </Column>

        <Column flexGrow={1.5}>
          <HeaderCell className="fw-bold text-black">
            <span title="Action">Action</span>
          </HeaderCell>
          <Cell>
            {(rowData: TableData) => (
              <div className="d-flex align-items-center justify-content-center gap-2">
                <button
                  className="btn btn-sm btn-outline-primary"
                  title="Download Invoice"
                  onClick={() => openInvoiceDownloadModal(rowData)}
                >
                  <FiDownload />
                </button>
                <button
                  className="btn btn-sm btn-outline-info"
                  title="Download Invoice As Pdf"
                  onClick={() => openInvoicePdfDownloadModal(rowData)}
                >
                  <FiFileText />
                </button>
                <button
                  className="btn btn-sm btn-outline-warning"
                  title="Download Time Task"
                  onClick={() => {
                    LibExport.DownloadExport(
                      AppRouteApi.Invoice.TimeAndTaskExcel(rowData?.sKey),
                      `Time-Task-${rowData.name}.xlsx`
                    );
                  }}
                >
                  <FiClock />
                </button>
                <SendMail
                  alreadySent={false}
                  value={rowData}
                  invoiceId={rowData.sKey || ""}
                  invoiceStartDate={rowData.startDate!}
                  invoiceEndDate={rowData.endDate!}
                  haveEmail={haveEmail}
                />
                {!rowData.isSubmitted && (
                  <>
                    <button
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => {
                        history.push(
                          AppRouteUi.Invoice?.Edit(rowData.sKey),
                          rowData
                        );
                      }}
                    >
                      <FiEdit />
                    </button>
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => handleDelete(rowData)}
                    >
                      <FiTrash2 />
                    </button>
                  </>
                )}
              </div>
            )}
          </Cell>
        </Column>
      </Table>

      {data && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <div className="d-flex align-items-center">
            <div className="me-2">Show</div>
            <PaginationEntriesInput
              onChange={(value) =>
                dispatch(ActionsInvoiceTable.setEntryCount(value as number))
              }
            />
            <div className="ms-2">entries</div>
          </div>

          <Pagination
            total={filteredTableData.length}
            pages={
              totalCount
                ? Math.ceil(totalCount / pageSize)
                : Math.ceil(filteredTableData.length / pageSize)
            }
            maxButtons={6}
            next={true}
            prev={true}
            activePage={pageNumber}
            onSelect={(page) => dispatch(ActionsInvoiceTable.goToPage(page))}
          />
        </div>
      )}
    </div>
  );
};
